import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ApplicationPermissionId,
  HasPermission,
  HasModulePermission,
  ModuleId,
} from "../utils/permissions";

type SidebarProps = {
  isOpen: boolean;
};

let lastOpenMenu: string | null = null;

const Sidebar = ({ isOpen }: SidebarProps) => {
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState<string | null>(() => lastOpenMenu);
  const canViewAdmin =
    HasModulePermission(ModuleId.Users) ||
    HasModulePermission(ModuleId.Roles) ||
    HasModulePermission(ModuleId.Workflow) ||
    HasModulePermission(ModuleId.MasterData) ||
    HasModulePermission(ModuleId.AuditLogs) ||
    HasModulePermission(ModuleId.Settings);

  const canViewWeddings =
    HasPermission(ModuleId.Weddings, ApplicationPermissionId.Apply) ||
    HasPermission(ModuleId.Weddings, ApplicationPermissionId.Approve) ||
    HasPermission(ModuleId.Weddings, ApplicationPermissionId.Print) ||
    HasPermission(ModuleId.Weddings, ApplicationPermissionId.Reprint);

  const canViewDivorces =
    HasPermission(ModuleId.Divorces, ApplicationPermissionId.Apply) ||
    HasPermission(ModuleId.Divorces, ApplicationPermissionId.Approve) ||
    HasPermission(ModuleId.Divorces, ApplicationPermissionId.Print) ||
    HasPermission(ModuleId.Divorces, ApplicationPermissionId.Reprint);

  const toggleMenu = (menuKey: string) => {
    setOpenMenu((current) => {
      const nextMenu = current === menuKey ? null : menuKey;
      lastOpenMenu = nextMenu;
      return nextMenu;
    });
  };

  return (
    <aside className={`sidebar ${isOpen ? "is-open" : ""}`}>
      <nav className="sidebar-nav">
        {HasPermission(ModuleId.Dashboard, ApplicationPermissionId.View) ? (
          <Link
            className={location.pathname === "/dashboard" ? "active" : ""}
            to="/dashboard"
          >
            <span className="nav-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1v-9Z" />
              </svg>
            </span>
            Dashboard
          </Link>
        ) : null}
        {canViewWeddings ? (
          <div className="nav-group">
            <button
              type="button"
              className="nav-parent"
              onClick={() => toggleMenu("marriages")}
              aria-expanded={openMenu === "marriages"}
            >
              <span className="nav-parent-content">
                <span className="nav-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                </span>
                Marriage
              </span>
              <span className="nav-toggle-icon">
                {openMenu === "marriages" ? "-" : "+"}
              </span>
            </button>
            {openMenu === "marriages" ? (
              <div className="nav-children">
                {HasPermission(
                  ModuleId.Weddings,
                  ApplicationPermissionId.Apply,
                ) ? (
                  <Link
                    to="/marriages/apply"
                    className={
                      location.pathname === "/marriages/apply" ? "active" : ""
                    }
                  >
                    Apply
                  </Link>
                ) : null}
                {HasPermission(
                  ModuleId.Weddings,
                  ApplicationPermissionId.Approve,
                ) ? (
                  <Link
                    to="/marriages/approval"
                    className={
                      location.pathname === "/marriages/approval"
                        ? "active"
                        : ""
                    }
                  >
                    Approve
                  </Link>
                ) : null}
                {HasPermission(
                  ModuleId.Weddings,
                  ApplicationPermissionId.Print,
                ) ? (
                  <Link
                    to="/marriages/print"
                    className={
                      location.pathname === "/marriages/print" ? "active" : ""
                    }
                  >
                    Print
                  </Link>
                ) : null}
                {HasPermission(
                  ModuleId.Weddings,
                  ApplicationPermissionId.View,
                ) ? (
                  <Link
                    to="/marriages/finalize"
                    className={
                      location.pathname === "/marriages/finalize"
                        ? "active"
                        : ""
                    }
                  >
                    Finalize
                  </Link>
                ) : null}
                {HasPermission(
                  ModuleId.Weddings,
                  ApplicationPermissionId.Reprint,
                ) ? (
                  <Link
                    to="/marriages/reprint"
                    className={
                      location.pathname === "/marriages/reprint" ? "active" : ""
                    }
                  >
                    Reprint
                  </Link>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        {canViewDivorces ? (
          <div className="nav-group">
            <button
              type="button"
              className="nav-parent"
              onClick={() => toggleMenu("divorces")}
              aria-expanded={openMenu === "divorces"}
            >
              <span className="nav-parent-content">
                <span className="nav-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M17 3H7a2 2 0 0 0-2 2v16l7-3 7 3V5a2 2 0 0 0-2-2zm-1 11H8v-2h8v2zm0-4H8V8h8v2z" />
                  </svg>
                </span>
                Divorce
              </span>
              <span className="nav-toggle-icon">
                {openMenu === "divorces" ? "-" : "+"}
              </span>
            </button>
            {openMenu === "divorces" ? (
              <div className="nav-children">
                {HasPermission(
                  ModuleId.Divorces,
                  ApplicationPermissionId.Apply,
                ) ? (
                  <Link
                    to="/divorce/apply"
                    className={
                      location.pathname === "/divorce/apply" ? "active" : ""
                    }
                  >
                    Apply
                  </Link>
                ) : null}
                {HasPermission(
                  ModuleId.Divorces,
                  ApplicationPermissionId.Approve,
                ) ? (
                  <Link
                    to="/divorce/approval"
                    className={
                      location.pathname === "/divorce/approval" ? "active" : ""
                    }
                  >
                    Approve
                  </Link>
                ) : null}
                {HasPermission(
                  ModuleId.Divorces,
                  ApplicationPermissionId.Print,
                ) ? (
                  <Link
                    to="/divorce/print"
                    className={
                      location.pathname === "/divorce/print" ? "active" : ""
                    }
                  >
                    Print
                  </Link>
                ) : null}
                {HasPermission(
                  ModuleId.Divorces,
                  ApplicationPermissionId.Reprint,
                ) ? (
                  <Link
                    to="/divorce/reprint"
                    className={
                      location.pathname === "/divorce/reprint" ? "active" : ""
                    }
                  >
                    Reprint
                  </Link>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        {canViewAdmin ? (
          <div className="nav-group">
            <button
              type="button"
              className="nav-parent"
              onClick={() => toggleMenu("admin")}
              aria-expanded={openMenu === "admin"}
            >
              <span className="nav-parent-content">
                <span className="nav-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M12 2 3 6v6c0 5 3.8 9.7 9 10 5.2-.3 9-5 9-10V6l-9-4Zm0 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm0 13c-2.3 0-4.5-1.1-6-2.8.1-2 4-3.2 6-3.2s5.9 1.2 6 3.2C16.5 18.9 14.3 20 12 20Z" />
                  </svg>
                </span>
                Admin
              </span>
              <span className="nav-toggle-icon">
                {openMenu === "admin" ? "-" : "+"}
              </span>
            </button>
            {openMenu === "admin" ? (
              <div className="nav-children">
                {HasPermission(ModuleId.Users, ApplicationPermissionId.View) ? (
                  <Link
                    to="/admin/users"
                    className={
                      location.pathname === "/admin/users" ? "active" : ""
                    }
                  >
                    Users
                  </Link>
                ) : null}
                {HasPermission(ModuleId.Roles, ApplicationPermissionId.View) ? (
                  <Link
                    to="/admin/roles"
                    className={
                      location.pathname === "/admin/roles" ? "active" : ""
                    }
                  >
                    Roles
                  </Link>
                ) : null}
                {HasPermission(
                  ModuleId.Workflow,
                  ApplicationPermissionId.View,
                ) ? (
                  <Link
                    to="/admin/workflows"
                    className={
                      location.pathname === "/admin/workflows" ? "active" : ""
                    }
                  >
                    Workflows
                  </Link>
                ) : null}
                {HasPermission(
                  ModuleId.AuditLogs,
                  ApplicationPermissionId.View,
                ) ? (
                  <Link
                    to="/admin/audit-logs"
                    className={
                      location.pathname === "/admin/audit-logs" ? "active" : ""
                    }
                  >
                    Audit Logs
                  </Link>
                ) : null}
                {HasPermission(
                  ModuleId.Settings,
                  ApplicationPermissionId.View,
                ) ? (
                  <Link
                    to="/admin/settings"
                    className={
                      location.pathname === "/admin/settings" ? "active" : ""
                    }
                  >
                    Settings
                  </Link>
                ) : null}
                {HasPermission(
                  ModuleId.MasterData,
                  ApplicationPermissionId.View,
                ) ? (
                  <Link
                    to="/admin/master-data"
                    className={
                      location.pathname === "/admin/master-data" ? "active" : ""
                    }
                  >
                    Master Data
                  </Link>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Users' Manual — visible to all authenticated users */}
        <Link
          className={`manual-nav-link${location.pathname === "/users-manual" ? " active" : ""}`}
          to="/users-manual"
        >
          <span className="nav-icon">📖</span>
          Users' Manual
        </Link>
      </nav>
    </aside>
  );
};

export default Sidebar;
